import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Loader2,
  PauseCircle,
  PlayCircle,
  Plus,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { useBeltProfiles } from '../../hooks/belts/useBelts';
import { useCompanySports } from '../../hooks/useCompanySports';
import { supabase } from '../../lib/supabase';
import {
  useAssignInitialRank,
  useStudentRanks,
  useSwitchActiveDiscipline,
  type StudentRankRow,
} from '../../hooks/belts/useStudentRanks';
import type { BeltProfile } from '../../types/belts';

/**
 * Per-student belt rank lifecycle page (#747).
 *
 * Lists every (sport) the student has a rank in, with state (active / on_hold)
 * and current rank. Owner/coach can:
 *   1. Assign an initial rank for a new discipline (with transfer-setup toggle).
 *   2. Switch which discipline is active (RPC swaps state atomically).
 *
 * Promotions / dismissals come from #749 — this page intentionally does NOT
 * surface those flows.
 */
export default function StudentRanksPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const ranks = useStudentRanks(studentId);
  const profiles = useBeltProfiles();
  const sports = useCompanySports();
  const switchActive = useSwitchActiveDiscipline();
  const assign = useAssignInitialRank();

  const [assignOpen, setAssignOpen] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // Sports where this student doesn't have a rank yet — i.e. assignable.
  const unassignedSports = useMemo(() => {
    if (!sports.data || !ranks.data) return [];
    const have = new Set(ranks.data.map((r) => r.sport_id));
    return sports.data.filter((s) => !have.has(s.id));
  }, [sports.data, ranks.data]);

  const onSwitch = async (targetSportId: string) => {
    if (!studentId) return;
    setSwitchError(null);
    try {
      await switchActive.mutateAsync({ studentId, targetSportId });
    } catch (e) {
      setSwitchError((e as Error).message);
    }
  };

  if (!studentId) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-white/70">No student id in URL.</div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Link
        to={ROUTES.STUDENTS}
        className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to students
      </Link>

      <div className="flex items-start gap-3">
        <Award className="w-6 h-6 text-emerald-400 mt-1" />
        <div>
          <h1 className="text-2xl font-bold text-[#EBFFEE]">Student ranks</h1>
          <p className="text-sm text-white/60 mt-1">
            Manage discipline assignments and active/on-hold state. Promotions
            are handled separately.
          </p>
        </div>
      </div>

      {ranks.isLoading && (
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {ranks.isError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-sm">
          {(ranks.error as Error).message}
        </div>
      )}

      {switchError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-sm">
          {switchError}
        </div>
      )}

      {!ranks.isLoading && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#EBFFEE]">Disciplines</h2>
            <button
              type="button"
              onClick={() => setAssignOpen(true)}
              disabled={unassignedSports.length === 0}
              title={
                unassignedSports.length === 0
                  ? 'Student already has a rank in every configured discipline.'
                  : 'Assign initial rank in another discipline'
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-30 disabled:cursor-not-allowed text-emerald-300 px-3 py-1.5 text-sm font-medium border border-emerald-500/30"
            >
              <Plus className="w-4 h-4" /> Assign discipline
            </button>
          </div>

          {(ranks.data ?? []).length === 0 ? (
            <p className="text-sm text-white/50">
              No disciplines assigned yet. Use &ldquo;Assign discipline&rdquo;
              to set the student&rsquo;s starting rank.
            </p>
          ) : (
            <div className="rounded-lg border border-white/10 divide-y divide-white/5">
              {(ranks.data ?? []).map((r) => (
                <StudentRankRowView
                  key={r.id}
                  row={r}
                  onSwitch={() => onSwitch(r.sport_id)}
                  isSwitching={switchActive.isPending}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {assignOpen && (
        <AssignDialog
          studentId={studentId}
          unassignedSports={unassignedSports}
          profiles={profiles.data ?? []}
          onClose={() => setAssignOpen(false)}
          assign={assign}
        />
      )}
    </div>
  );
}

function StudentRankRowView({
  row,
  onSwitch,
  isSwitching,
}: {
  row: StudentRankRow;
  onSwitch: () => void;
  isSwitching: boolean;
}) {
  const isActive = row.state === 'active';
  const Icon = isActive ? PlayCircle : PauseCircle;
  const stateBadge = isActive
    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
    : 'bg-amber-500/10 border-amber-500/30 text-amber-300';

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]">
      <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-amber-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#EBFFEE]">{row.sport_name ?? 'Discipline'}</span>
          <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${stateBadge}`}>
            {row.state}
          </span>
        </div>
        <div className="text-xs text-white/60 mt-0.5 truncate">
          {row.rank_name ?? 'Unknown rank'}
          {row.profile_name ? ` · ${row.profile_name}` : ''}
          {' · achieved '}
          {new Date(row.achieved_at).toLocaleDateString()}
        </div>
      </div>
      {!isActive && (
        <button
          type="button"
          onClick={onSwitch}
          disabled={isSwitching}
          className="inline-flex items-center gap-1 text-sm text-emerald-300 hover:text-emerald-200 disabled:opacity-40 px-2 py-1"
        >
          {isSwitching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
          Make active
        </button>
      )}
    </div>
  );
}

function AssignDialog({
  studentId,
  unassignedSports,
  profiles,
  onClose,
  assign,
}: {
  studentId: string;
  unassignedSports: { id: string; sport_name: string }[];
  profiles: BeltProfile[];
  onClose: () => void;
  assign: ReturnType<typeof useAssignInitialRank>;
}) {
  const [sportId, setSportId] = useState('');
  const [isTransferSetup, setIsTransferSetup] = useState(false);
  const [setActive, setSetActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The user can optionally pick a specific rank (instead of defaulting to
  // lowest). Listed from the company-owned profile bound to the chosen sport.
  const candidateProfile = useMemo(
    () => profiles.find((p) => !p.is_system && p.sport_id === sportId),
    [profiles, sportId]
  );
  const [rankId, setRankId] = useState<string>('');
  const ranksQuery = useProfileRanks(candidateProfile?.id);

  const submit = async () => {
    setError(null);
    if (!sportId) {
      setError('Pick a discipline.');
      return;
    }
    try {
      await assign.mutateAsync({
        studentId,
        sportId,
        rankId: rankId || null,
        isTransferSetup,
        setActive,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f1612] border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-[#EBFFEE]">
            Assign initial rank
          </h3>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">Discipline</label>
          <select
            value={sportId}
            onChange={(e) => {
              setSportId(e.target.value);
              setRankId('');
            }}
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
          >
            <option value="">Pick…</option>
            {unassignedSports.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sport_name}
              </option>
            ))}
          </select>
        </div>

        {sportId && !candidateProfile && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
            No company-owned belt profile is bound to this discipline yet. Clone
            a template at <Link to={ROUTES.BELTS} className="underline">Belts &amp; Ranks</Link> first.
          </div>
        )}

        {candidateProfile && (
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-white/50">
              Starting rank (optional — defaults to first)
            </label>
            <select
              value={rankId}
              onChange={(e) => setRankId(e.target.value)}
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">Default (lowest rank)</option>
              {(ranksQuery.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={isTransferSetup}
            onChange={(e) => setIsTransferSetup(e.target.checked)}
            className="accent-emerald-500"
          />
          Transfer setup (student joined from another gym at this rank)
        </label>

        <label className="flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={setActive}
            onChange={(e) => setSetActive(e.target.checked)}
            className="accent-emerald-500"
          />
          Make this the student&rsquo;s active discipline
        </label>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50 resize-none"
          />
        </div>

        {error && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={assign.isPending || !sportId || !candidateProfile}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black px-3 py-1.5 text-sm font-medium"
          >
            {assign.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

// Tiny inline helper — only used by AssignDialog, not worth its own hook file.
function useProfileRanks(profileId: string | undefined) {
  return useQuery({
    queryKey: ['belt-ranks-for-profile', profileId ?? 'none'],
    queryFn: async () => {
      if (!profileId) return [];
      const { data, error } = await supabase
        .from('belt_ranks')
        .select('id, name, rank_order')
        .eq('profile_id', profileId)
        .order('rank_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileId,
    staleTime: 60_000,
  });
}
